
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { CharacterVoice, VoiceMode, QwenPresetSpeaker, QwenTTSLanguage, SavedVoiceSample } from '../../types';
import { createCharacterVoice, saveCharacterVoice, uploadReferenceAudio, uploadSavedVoiceAudio, saveSavedVoiceSample, createSavedVoiceSample } from '../../lib/characterVoiceService';
import { generateVoicePreview } from '../../lib/voiceGeneration';
import { useToast } from '../../context/ToastContext';
import { CharacterArchetype } from '../../lib/physiognomyVoicePrompt';
import { MagicWandIcon, MicrophoneIcon, UserIcon } from '@phosphor-icons/react';

// Reusing the same TEST_PHRASES as found in InlineVoiceConfigurator.
// In a full refactor, these should be moved to a shared constant file.
const TEST_PHRASES: { quote: string; direction: string }[] = [
    // Shortened list for brevity in this hook file, referring to the main implementation if needed, 
    // but for full functionality we should probably duplicate or import.
    // Ideally, these are imported. Since I can't easily modify the imports of other files without touching them,
    // I will assume for now we use a default set or duplicated set.
    // Actually, to ensure consistency, let's include the full set.
    {
        quote: "My mama always said life was like a box of puzzles. You never know which piece you're gonna need next. And sometimes, sometimes the piece you've been looking for was in your pocket the whole time. I've been running for a long time now. Not away from anything, just running. And somewhere along the way I realized that foolish is as foolish does, but so is brave, and so is kind. People kept asking me why I kept going. I just felt like it. That's what I told them. But the truth is, I was running toward something. I just didn't know what it was yet. Turns out it was right here all along.",
        direction: "Simple and innocent at the start, like a child sharing wisdom they don't fully understand. Let the profundity sneak up naturally. Slow down at 'right here'—let the emotion catch in your throat. End with quiet wonder."
    },
    {
        quote: "I'm gonna make you a proposition, and I want you to think very carefully before you answer. This isn't business. This is personal. In my family, we have long memories. We don't forget easily, but we also know how to forgive. My father taught me that a person who doesn't spend time with their family can never truly know themselves. I didn't understand that when I was young. I thought influence was everything. Turns out, influence without people you trust is just a very lonely throne. So here's what I'm offering you. Not a deal. Not a favor you'll owe me someday. I'm offering you a seat at my table. Because the family you choose matters just as much as the family you're born into.",
        direction: "Low and deliberate, each word chosen carefully. Command respect without raising your voice. Let 'lonely throne' land with genuine regret. Shift to warmth when offering the seat—this is an honor, make them feel it."
    },
    {
        quote: "They can take our comfort. They can take our certainty. They can take everything we've built and watch it crumble. But they cannot, they will never, take our spirit. I know you're tired. I know some of you want to walk away, and honestly, I wouldn't blame you if you did. But ask yourself this: when you're old, sitting in your chair, thinking about this moment, what do you want to remember? That you played it safe? Or that you stood up when it mattered? Every person who ever changed anything was told it was impossible first. They did it anyway. So yes, we might fail. But at least we'll fail standing up, not bowing down. Now who's with me?",
        direction: "Start with controlled anger, listing what they can take. Build intensity with each line. Let 'take our spirit' ring out like a battle cry. Soften for the vulnerable admission about being tired. End with a rallying crescendo—this is the moment before the charge."
    },
    {
        quote: "Let me tell you something about hope. Some people say it's dangerous. That it can drive you crazy, make you do reckless things. Maybe they're right. But I'll tell you what's more dangerous: giving up. I've seen what giving up does to people. It doesn't end you all at once. It ends you slowly, day by day, until one morning you wake up and realize you stopped living years ago. Hope is a good thing, maybe the best of things. And good things have a way of surviving. So you can build your walls. You can tell me it's impossible. But I'm gonna keep believing anyway. Because the alternative isn't living. It's just waiting.",
        direction: "Contemplative and hard-won wisdom at the start—you've earned this perspective. Build quiet intensity when describing giving up. Let 'good things have a way of surviving' shine with conviction. End defiant but peaceful—certain without being aggressive."
    },
    {
        quote: "I know you didn't ask for this. None of us ask for the challenges that find us. You wish this responsibility had fallen to someone else, someone stronger, someone wiser. So do all who live to see such times. But that is not for us to decide. All we have to decide is what to do with the time that is given to us. There are forces at work in this world beyond our understanding. And sometimes, the most unlikely person can change the course of everything. Not through great power or ancient wisdom. But through simple acts of courage and kindness. That is your task now. Not to be mighty. Just to be brave enough to take the next step.",
        direction: "Gentle and fatherly, like a mentor who's seen much. Validate their fear before redirecting it. Let 'most unlikely person' warm with affection. End with quiet encouragement—you believe in them even if they don't believe in themselves yet."
    },
    {
        quote: "Hey. Look at me. What happened to you wasn't your fault. I know you've heard that before and it didn't mean anything. But I need you to hear it now. Really hear it. You were just trying to survive. And somewhere along the way, you convinced yourself that you deserved what happened. That there was something wrong inside you. There isn't. There never was. You're not broken. You're not damaged. You're someone who went through something terrible and came out the other side still fighting. That's not weakness. That's the definition of strength. It wasn't your fault. Say it back to me. It wasn't your fault.",
        direction: "Intense eye contact energy—don't let them look away. Start firm but caring. Build emotional pressure with each sentence. Let your voice crack slightly at 'just trying to survive.' End repeating the phrase with increasing tenderness until it breaks through."
    },
    {
        quote: "Seize the day. I know it sounds like something you'd find on a poster in a dentist's office, but stay with me here. Walk over to a mirror sometime. Look at yourself. Really look. That face looking back at you, that's the only you there's ever going to be. No do-overs, no second takes. This is it. And one day, sooner than you think, that face will be gone. So the question isn't whether you have time to chase what matters to you. The question is whether you're brave enough to start. Because the only thing worse than failing is wondering what would have happened if you'd just had the courage to begin. So begin. Today. Right now.",
        direction: "Start almost casual, like you're about to share a secret. Build urgency when confronting mortality—not morbid, but vibrantly alive. Let passion crescendo at 'brave enough to start.' End with quiet command—this isn't a suggestion, it's a call to action."
    },
    {
        quote: "You want to know the truth about being the one everyone counts on? It's lonely. Really lonely. You either become what everyone needs you to be, or you watch everything fall apart. And once you start, you can't stop. Because the moment you do, people get hurt. I used to think the goal was to be appreciated. Now I understand. Sometimes the goal is just to be useful. To hold things together so others don't have to. They might not thank you. They might even resent you for the hard choices. But that's the responsibility you accepted. You either step down while people still think well of you, or you stay long enough to make the decisions nobody else will. I made my choice. Now you have to make yours.",
        direction: "Weary and battle-worn from the start. Not self-pitying, just honest about the cost. Let the loneliness be palpable. Build to quiet resignation at 'that's the responsibility.' End looking them in the eye—passing wisdom, warning them what this path costs."
    },
    {
        quote: "Let me tell you something you already know. The world ain't all sunshine and rainbows. It's a mean and difficult place, and it will knock you down and keep you there if you let it. Nobody is gonna hit as hard as life itself. But it ain't about how hard you get hit. It's about how hard you can get hit and keep moving forward. How much you can take and keep going. That's how you succeed. Now if you know what you're worth, then go out and get what you're worth. But you gotta be willing to take the hits. And not point fingers saying you ain't where you want to be because of someone else. That's what cowards do, and that ain't you. I know it ain't you.",
        direction: "Start gruff and direct—no sugarcoating the reality. Build intensity through the hitting metaphor. Let 'keep moving forward' punch through with conviction. End pointing at them—this is personal, this is about THEM, and you genuinely believe in them."
    },
    {
        quote: "I need you to promise me something. When you get through this, and you will get through this, I need you to live. Really live. Have adventures, make memories, grow old surrounded by people who love you. Not stuck in this moment. Not defined by this. Promise me. You're going to go on and build something beautiful. And when you're old, looking back, you'll know that this moment mattered. That we mattered. But you can't stay frozen here. You have to keep moving forward. I know it's hard. I know you're scared. But some people come into our lives to stay forever, and some come to show us what's possible. Let me show you what's possible. Now go live it.",
        direction: "Urgent and breathless at first—time is short. Soften into tenderness at 'promise me.' Let your voice warm when painting the future they deserve. Crack with emotion at 'we mattered.' End with peaceful acceptance—you've made peace with your role in their story."
    },
];

export type TabId = 'custom' | 'clone' | 'design';

export const TABS: { id: TabId; label: string; mode: VoiceMode; icon: React.ElementType; description: string }[] = [
    { id: 'design', label: 'Design', mode: 'voice_design', icon: MagicWandIcon, description: 'Describe your ideal voice' },
    { id: 'clone', label: 'Clone', mode: 'voice_clone', icon: MicrophoneIcon, description: 'Clone from audio sample' },
    { id: 'custom', label: 'Preset', mode: 'custom_voice', icon: UserIcon, description: 'Choose from 9 preset voices' },
];

export interface UseVoiceConfiguratorProps {
    characterId: string;
    userId: string;
    projectId: string;
    characterName?: string;
    characterDescription?: string;
    existingVoice?: CharacterVoice | null;
    onVoiceSaved: (voice: CharacterVoice) => void;
    onVoiceDeleted?: () => void;
    // Character images for face-to-voice generation
    headImageUrl?: string;
    bodyImageUrl?: string;
    referenceSheetUrl?: string;
    archetype?: CharacterArchetype;
    // Saved voice sample props
    selectedSavedSample?: SavedVoiceSample | null;
    onSampleSaved?: () => void; // Called when a new sample is saved (to refresh list)
    onClearSelection?: () => void; // Called when user wants to create new (clears selected sample)
}

export function useVoiceConfigurator({
    characterId,
    userId,
    projectId,
    characterName,
    characterDescription,
    existingVoice,
    onVoiceSaved,
    onVoiceDeleted,
    selectedSavedSample,
    onSampleSaved,
    onClearSelection,
}: UseVoiceConfiguratorProps) {
    const { showToast } = useToast();
    const audioRef = useRef<HTMLAudioElement>(null);

    // Random test phrase placeholder - changes after each test
    const [phraseIndex, setPhraseIndex] = useState(() => Math.floor(Math.random() * TEST_PHRASES.length));

    // Get phrase with character name substituted
    const getProcessedPhrase = useCallback((index: number): { quote: string; direction: string } => {
        const phraseData = TEST_PHRASES[index];
        let quote = phraseData.quote;

        if (characterName && quote.includes('{name}')) {
            quote = quote.replace(/{name}/g, characterName);
        } else if (quote.includes('{name}')) {
            // If phrase needs name but no name provided, pick a different phrase without {name}
            const genericPhrases = TEST_PHRASES.filter(p => !p.quote.includes('{name}'));
            const fallback = genericPhrases[index % genericPhrases.length];
            return { quote: fallback.quote, direction: fallback.direction };
        }

        return { quote, direction: phraseData.direction };
    }, [characterName]);

    // Current phrase and direction for placeholders (used as actual test text when input is empty)
    const currentPhrase = useMemo(() => getProcessedPhrase(phraseIndex), [phraseIndex, getProcessedPhrase]);
    const testLinePlaceholder = currentPhrase.quote;
    const directionPlaceholder = currentPhrase.direction;

    // Pick a new random phrase (called after test completes)
    const pickNewPhrase = useCallback(() => {
        setPhraseIndex(prev => {
            let newIndex;
            do {
                newIndex = Math.floor(Math.random() * TEST_PHRASES.length);
            } while (newIndex === prev && TEST_PHRASES.length > 1);
            return newIndex;
        });
    }, []);

    // Expanded state - start expanded if no voice exists
    const [isExpanded, setIsExpanded] = useState(!existingVoice);

    // Determine initial tab from existing voice
    const getInitialTab = (): TabId => {
        if (!existingVoice) return 'design';
        switch (existingVoice.voice_mode) {
            case 'custom_voice': return 'custom';
            case 'voice_clone': return 'clone';
            case 'voice_design': return 'design';
            default: return 'design';
        }
    };

    const [activeTab, setActiveTab] = useState<TabId>(getInitialTab());

    // Form state
    const [language, setLanguage] = useState<QwenTTSLanguage>(existingVoice?.language || 'Auto');
    const [styleInstruction, setStyleInstruction] = useState(existingVoice?.style_instruction || '');

    // Custom voice state
    const [selectedSpeaker, setSelectedSpeaker] = useState<QwenPresetSpeaker | undefined>(
        existingVoice?.preset_speaker
    );

    // Voice clone state
    const [referenceAudioBase64, setReferenceAudioBase64] = useState<string | undefined>(
        existingVoice?.reference_audio_url
    );
    const [referenceTranscript, setReferenceTranscript] = useState(existingVoice?.reference_transcript || '');
    const [referenceAudioDuration, setReferenceAudioDuration] = useState(0);

    // Voice design state
    const [voiceDescription, setVoiceDescription] = useState(existingVoice?.voice_description || '');

    // Test state
    const [testLine, setTestLine] = useState('');
    const [testAudioBlob, setTestAudioBlob] = useState<Blob | null>(null);
    const [testAudioUrl, setTestAudioUrl] = useState<string | null>(null); // For saved sample audio
    const [isPlayerExiting, setIsPlayerExiting] = useState(false);
    const [isGeneratingTest, setIsGeneratingTest] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPlayingTest, setIsPlayingTest] = useState(false);

    // Track if we're editing a saved sample
    const [editingSampleId, setEditingSampleId] = useState<string | null>(null);

    // Load selected saved sample into form
    useEffect(() => {
        if (selectedSavedSample) {
            // Load the sample's data into the form
            setTestLine(selectedSavedSample.quote);
            setStyleInstruction(selectedSavedSample.vocal_direction || '');
            setTestAudioUrl(selectedSavedSample.audio_url);
            setTestAudioBlob(null); // Clear blob, we'll use URL
            setEditingSampleId(selectedSavedSample.id);

            // Load voice config
            const config = selectedSavedSample.voice_config;
            setLanguage(config.language);
            if (config.voice_mode === 'custom_voice' && config.preset_speaker) {
                setActiveTab('custom');
                setSelectedSpeaker(config.preset_speaker);
            } else if (config.voice_mode === 'voice_clone') {
                setActiveTab('clone');
                setReferenceAudioBase64(config.reference_audio_url);
                setReferenceTranscript(config.reference_transcript || '');
            } else if (config.voice_mode === 'voice_design') {
                setActiveTab('design');
                setVoiceDescription(config.voice_description || '');
            }
        } else {
            // Clear editing state and form when no sample selected (Create New clicked)
            setEditingSampleId(null);
            setTestLine('');
            setStyleInstruction('');
            setTestAudioBlob(null);
            setTestAudioUrl(null);
        }
    }, [selectedSavedSample]);

    // Get current mode
    const currentMode = TABS.find(t => t.id === activeTab)?.mode || 'voice_design';

    // Validation
    const isValid = useCallback(() => {
        switch (currentMode) {
            case 'custom_voice':
                return !!selectedSpeaker;
            case 'voice_clone':
                return !!referenceAudioBase64;
            case 'voice_design':
                return voiceDescription.trim().length > 10;
            default:
                return false;
        }
    }, [currentMode, selectedSpeaker, referenceAudioBase64, voiceDescription]);

    // Generate preview
    const handleGenerateTest = async () => {
        if (!isValid()) {
            showToast('Please complete the voice configuration first', 'error');
            return;
        }

        setIsGeneratingTest(true);

        // Fade out existing player before clearing
        if (testAudioBlob) {
            setIsPlayerExiting(true);
            await new Promise(resolve => setTimeout(resolve, 200)); // Wait for fade-out animation
        }
        setTestAudioBlob(null);
        setIsPlayerExiting(false);

        // Use placeholder phrase and direction if user hasn't entered custom text
        const textToTest = testLine.trim() || testLinePlaceholder;
        const usedPlaceholder = !testLine.trim();
        // Use placeholder direction when using placeholder quote and no custom direction
        const directionToUse = styleInstruction.trim() || (usedPlaceholder ? directionPlaceholder : '');

        try {
            const result = await generateVoicePreview(currentMode, {
                speaker: selectedSpeaker,
                voiceDescription,
                referenceAudio: referenceAudioBase64,
                referenceText: referenceTranscript,
                language,
                styleInstruction: directionToUse,
                previewText: textToTest,
                characterName,
            });

            if (result.audioBlob) {
                setTestAudioBlob(result.audioBlob);
            } else if (result.audioUrl) {
                const response = await fetch(result.audioUrl);
                const blob = await response.blob();
                setTestAudioBlob(blob);
            }

            // Populate input fields with the text that was actually used
            if (usedPlaceholder) {
                setTestLine(textToTest);
                if (!styleInstruction.trim() && directionToUse) {
                    setStyleInstruction(directionToUse);
                }
                // Pick a new random phrase for next time
                pickNewPhrase();
            }

            showToast('Test audio generated!', 'success');
        } catch (error) {
            console.error('Failed to generate test audio:', error);
            showToast('Failed to generate test audio. Please try again.', 'error');
        } finally {
            setIsGeneratingTest(false);
        }
    };

    // Play existing preview
    const handlePlayExisting = () => {
        if (!existingVoice?.preview_audio_url || !audioRef.current) return;

        if (isPlayingTest) {
            audioRef.current.pause();
            setIsPlayingTest(false);
        } else {
            audioRef.current.play().catch(console.warn);
            setIsPlayingTest(true);
        }
    };

    // Save voice configuration
    const handleSave = async () => {
        if (!isValid()) {
            showToast('Please complete the voice configuration', 'error');
            return;
        }

        // Get the actual text used (either user input or placeholder)
        const actualQuote = testLine.trim() || testLinePlaceholder;
        const actualDirection = styleInstruction.trim() || (!testLine.trim() ? directionPlaceholder : '');

        // Must have audio to save
        if (!testAudioBlob && !testAudioUrl) {
            showToast('Please test the voice first to generate audio', 'error');
            return;
        }

        setIsSaving(true);

        try {
            // First, save/update the CharacterVoice configuration
            const voice = createCharacterVoice(characterId, userId, {
                id: existingVoice?.id,
                voice_mode: currentMode,
                language,
                style_instruction: styleInstruction || undefined,
                preset_speaker: currentMode === 'custom_voice' ? selectedSpeaker : undefined,
                reference_audio_url: currentMode === 'voice_clone' ? referenceAudioBase64 : undefined,
                reference_transcript: currentMode === 'voice_clone' ? referenceTranscript : undefined,
                voice_description: currentMode === 'voice_design' ? voiceDescription : undefined,
                created_at: existingVoice?.created_at || Date.now(),
            });

            // Upload reference audio to R2 if it's a new base64 (for voice clone)
            let finalReferenceAudioUrl = voice.reference_audio_url;
            if (currentMode === 'voice_clone' && referenceAudioBase64?.startsWith('data:')) {
                const match = referenceAudioBase64.match(/^data:([^;]+);base64,(.+)$/);
                if (match) {
                    const mimeType = match[1];
                    const base64 = match[2];
                    const binaryString = atob(base64);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    const blob = new Blob([bytes], { type: mimeType });
                    const url = await uploadReferenceAudio(blob, userId, projectId, characterId);
                    voice.reference_audio_url = url;
                    finalReferenceAudioUrl = url;
                }
            }

            // Upload preview audio if generated (new blob)
            let savedAudioUrl = testAudioUrl; // Use existing URL if editing
            if (testAudioBlob) {
                savedAudioUrl = await uploadSavedVoiceAudio(testAudioBlob, userId, projectId, characterId);
                voice.preview_audio_url = savedAudioUrl;
                voice.preview_text = actualQuote.substring(0, 100);
            }

            await saveCharacterVoice(voice);

            // Now save the SavedVoiceSample
            const voiceConfig: SavedVoiceSample['voice_config'] = {
                voice_mode: currentMode,
                language,
                preset_speaker: currentMode === 'custom_voice' ? selectedSpeaker : undefined,
                reference_audio_url: currentMode === 'voice_clone' ? finalReferenceAudioUrl : undefined,
                reference_transcript: currentMode === 'voice_clone' ? referenceTranscript : undefined,
                voice_description: currentMode === 'voice_design' ? voiceDescription : undefined,
            };

            const sample = createSavedVoiceSample(
                characterId,
                userId,
                savedAudioUrl!,
                actualQuote,
                voiceConfig,
                {
                    id: editingSampleId || undefined, // Preserve ID if editing
                    vocal_direction: actualDirection || undefined,
                }
            );

            await saveSavedVoiceSample(sample);

            showToast('Voice saved successfully!', 'success');
            setIsExpanded(false);
            onVoiceSaved(voice);
            onSampleSaved?.(); // Notify parent to refresh saved samples list

            // Clear the form for next creation
            setTestLine('');
            setStyleInstruction('');
            setTestAudioBlob(null);
            setTestAudioUrl(null);
            setEditingSampleId(null);
            onClearSelection?.();
            pickNewPhrase(); // Get a new random phrase
        } catch (error) {
            console.error('Failed to save voice:', error);
            showToast('Failed to save voice. Please try again.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        isExpanded,
        setIsExpanded,
        activeTab,
        setActiveTab,
        language,
        setLanguage,
        styleInstruction,
        setStyleInstruction,
        selectedSpeaker,
        setSelectedSpeaker,
        referenceAudioBase64,
        setReferenceAudioBase64,
        referenceTranscript,
        setReferenceTranscript,
        referenceAudioDuration,
        setReferenceAudioDuration,
        voiceDescription,
        setVoiceDescription,
        testLine,
        setTestLine,
        testLinePlaceholder,
        directionPlaceholder,
        testAudioBlob,
        testAudioUrl,
        isPlayerExiting,
        isGeneratingTest,
        isSaving,
        isPlayingTest,
        audioRef,
        isValid,
        handleGenerateTest,
        handlePlayExisting,
        handleSave,
        existingVoice,
        selectedSavedSample,
    };
}
